import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import aviso, { SEM_SERVIDOR } from '../lib/aviso';
import EmojiPicker, { Theme, type EmojiClickData } from 'emoji-picker-react';
import {
    LuBraces, LuChevronLeft, LuCircleAlert, LuCircleCheck, LuCircleDashed, LuCircleX, LuClock, LuCopy,
    LuFileText, LuIdCard, LuImage, LuMapPin, LuMegaphone, LuPencil, LuPlus,
    LuSend, LuSmile, LuTag, LuTextQuote, LuReply, LuTrash2, LuTriangleAlert, LuUpload, LuVideo, LuX, LuHistory,
} from 'react-icons/lu';
import PageHeader from '../components/PageHeader';
import SecaoHistorico from '../components/historico/SecaoHistorico';
import Select from '../components/Select';
import Dialog from '../components/Dialog';
import CelularWhatsApp from '../components/CelularWhatsApp';
import { useEmpresaDaFolha } from '../components/documentos/useEmpresaDaFolha';
import TemplateBubble, {
    lerComponentes, type FormatoHeader, type TemplateVisual,
} from '../components/TemplateBubble';
import { useTheme } from '../contexts/ThemeContext';
import WhatsAppConnectionGuard from '../components/WhatsAppConnectionGuard';
import { PERMISSOES, temPermissao } from '../config/permissions';
import {
    MetaTemplateService, type BotaoTemplate, type CreateTemplateDTO, type MetaTemplate,
} from '../services/MetaTemplateService';
import { CampaignService, type Campaign } from '../services/CampaignService';
import {
    EDITAVEL, IDIOMAS, rotuloIdioma, rotuloQualidade, STATUS_LABEL, STATUS_TOM, tomQualidade,
} from '../utils/metaTemplateLabels';
import './TemplatesScreen.css';

/* ─── Domínio ──────────────────────────────────────────────────── */

type TipoBotao = BotaoTemplate['tipo'];

const FORMATOS = [
    { value: 'NONE', label: 'Sem cabeçalho' },
    { value: 'TEXT', label: 'Texto' },
    { value: 'IMAGE', label: 'Imagem' },
    { value: 'VIDEO', label: 'Vídeo' },
    { value: 'DOCUMENT', label: 'Documento' },
    { value: 'LOCATION', label: 'Localização' },
];

const TIPOS_BOTAO: Array<{ value: TipoBotao; label: string }> = [
    { value: 'QUICK_REPLY', label: 'Resposta rápida' },
    { value: 'URL', label: 'Link (URL)' },
    { value: 'PHONE_NUMBER', label: 'Telefone' },
    { value: 'VOICE_CALL', label: 'Chamada de voz' },
    { value: 'COPY_CODE', label: 'Copiar código' },
    { value: 'FLOW', label: 'WhatsApp Flow' },
];

const MEDIA_ACCEPT: Record<string, string> = {
    IMAGE: 'image/jpeg,image/png',
    VIDEO: 'video/mp4,video/3gpp',
    DOCUMENT: 'application/pdf,application/msword,.docx,.pptx,.xlsx',
};

const MAX_BOTOES = 10;
const MAX_CORPO = 1024;
const MAX_RODAPE = 60;

type SecaoId = 'nome' | 'cabecalho' | 'corpo' | 'rodape' | 'botoes';

const SECOES: Array<{ id: SecaoId; label: string; icone: typeof LuIdCard }> = [
    { id: 'nome', label: 'Nome e idioma', icone: LuIdCard },
    { id: 'cabecalho', label: 'Cabeçalho', icone: LuImage },
    { id: 'corpo', label: 'Corpo', icone: LuTextQuote },
    { id: 'rodape', label: 'Rodapé', icone: LuTag },
    { id: 'botoes', label: 'Botões', icone: LuReply },
];

type EstadoUpload = 'idle' | 'uploading' | 'done' | 'error';

/** Uma linha da caixa "Antes de enviar". `espera` preenchido vence `ok`: é o
 *  item que ainda não tem insumo para ser conferido, e aparece em cinza. */
interface Checagem {
    espera: string | null;
    ok: boolean;
    texto: string;
    detalhe: string;
}

interface Formulario {
    nome: string;
    idioma: string;
    formatoHeader?: FormatoHeader;
    textoHeader: string;
    exemploHeaderTexto: string;
    exemploHeaderHandle: string;
    caminhoMidiaHeader: string;
    textoBody: string;
    textoFooter: string;
}

const VAZIO: Formulario = {
    nome: '', idioma: 'pt_BR', formatoHeader: undefined, textoHeader: '',
    exemploHeaderTexto: '', exemploHeaderHandle: '', caminhoMidiaHeader: '',
    textoBody: '', textoFooter: '',
};

/** O nome vai para a Meta em snake_case; a tela mostra o que vai ser enviado. */
const paraSlug = (v: string) =>
    v.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');

/**
 * Os botões como o FORMULÁRIO precisa deles.
 *
 * `lerComponentes` (do `TemplateBubble`) devolve só tipo e texto, que é o que
 * a bolha desenha. Aqui falta o resto — endereço, telefone, código, Flow —, e
 * sem ele editar um template com botão de link reenviava o botão **sem a URL**.
 * O formato dos campos é o da Meta, que é como o `componentesJson` vem.
 */
const lerBotoes = (componentesJson: string | null): BotaoTemplate[] => {
    if (!componentesJson) return [];
    let comps: Array<Record<string, unknown>> = [];
    try {
        const lido = JSON.parse(componentesJson);
        if (!Array.isArray(lido)) return [];
        comps = lido;
    } catch {
        return [];
    }

    const bloco = comps.find(c => c?.type === 'BUTTONS');
    const lista = (bloco?.buttons as Array<Record<string, unknown>> | undefined) ?? [];

    return lista.map(b => {
        const tipo = ((b?.type as string) ?? 'QUICK_REPLY') as TipoBotao;
        const botao: BotaoTemplate = { tipo, texto: (b?.text as string) ?? '' };

        if (tipo === 'URL') botao.url = (b?.url as string) ?? '';
        if (tipo === 'PHONE_NUMBER') botao.telefone = (b?.phone_number as string) ?? '';
        if (tipo === 'COPY_CODE') {
            /* COPY_CODE vai e volta em `example: [codigo]` e sem `text` — o
               rótulo do botão é da própria Meta. O formulário exige um texto,
               então entra o mesmo que o cliente vê. */
            const exemplo = b?.example;
            botao.codigoOferta = Array.isArray(exemplo)
                ? ((exemplo[0] as string) ?? '')
                : ((exemplo as string) ?? '');
            if (!botao.texto) botao.texto = 'Copiar código';
        }
        if (tipo === 'FLOW') {
            botao.flow_id = (b?.flow_id as string) ?? '';
            botao.flow_action = (b?.flow_action as 'NAVIGATE' | 'DATA_EXCHANGE') ?? 'NAVIGATE';
            botao.navigate_screen = (b?.navigate_screen as string) ?? '';
        }
        return botao;
    });
};

const dataCurta = (iso: string | null) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('pt-BR');
};

const desdeAgora = (iso: string | null) => {
    if (!iso) return 'nunca';
    const ms = Date.now() - new Date(iso).getTime();
    if (Number.isNaN(ms)) return 'nunca';
    const min = Math.round(ms / 60000);
    if (min < 1) return 'agora';
    if (min < 60) return `há ${min} min`;
    const h = Math.round(min / 60);
    if (h < 24) return `há ${h} h`;
    return dataCurta(iso);
};

/** O motivo da rejeição em texto de tela (a API traduz o código da Meta) e o código cru
    embaixo, para quem precisar procurar. O detalhe da Meta não chega ao CRM: só o código. */
const MotivoRejeicao = ({ template }: { template: MetaTemplate }) => (
    <div className="tpl-ck is-bad" style={{ borderRadius: 6 }}>
        <LuCircleX size={13} />
        <span>
            <b>{template.motivoRejeicaoTitulo ?? template.motivoRejeicao}</b>
            <span className="tpl-ck-exp">
                {template.motivoRejeicaoExplicacao && <>{template.motivoRejeicaoExplicacao} </>}
                O detalhe fica no WhatsApp Manager, em Modelos de mensagem.
            </span>
            <span className="tpl-ck-cod">Código da Meta: {template.motivoRejeicao}</span>
        </span>
    </div>
);

/* ─── Página ───────────────────────────────────────────────────── */

const TemplateEditorPage = () => {
    const { id } = useParams<{ id: string }>();
    const [busca] = useSearchParams();
    const navegar = useNavigate();
    const { theme } = useTheme();
    const novo = id === 'novo';
    /* Sem `marketing_templates.gerenciar` o template só é lido: somem criar,
       editar, duplicar e excluir (a API recusa sem a chave). */
    const podeGerenciar = temPermissao(PERMISSOES.marketingTemplatesGerenciar);

    /* `?editar=1` é a porta do lápis da listagem: ele abre a página já em
       edição, sem passar pela leitura. Fica na URL (e não em `state` de
       rota) para sobreviver a recarregar a página. */
    const abrirEditando = busca.get('editar') === '1';

    /* `?de=<id>` é a porta do Duplicar: abre o formulário de template NOVO já
       preenchido com o conteúdo de outro. Fica na URL pelo mesmo motivo do
       `?editar=1` — sobrevive a recarregar a página. */
    const copiarDe = busca.get('de');

    const [template, setTemplate] = useState<MetaTemplate | null>(null);
    const [carregando, setCarregando] = useState(!novo);
    const [modo, setModo] = useState<'ver' | 'editar'>(novo ? 'editar' : 'ver');
    const [salvando, setSalvando] = useState(false);

    const [form, setForm] = useState<Formulario>(VAZIO);
    const [exemplos, setExemplos] = useState<Record<string, string>>({});
    const [botoes, setBotoes] = useState<BotaoTemplate[]>([]);
    const [erros, setErros] = useState<Record<string, string>>({});

    const [upload, setUpload] = useState<EstadoUpload>('idle');
    const [arquivoNome, setArquivoNome] = useState('');
    const [previaLocal, setPreviaLocal] = useState<string | undefined>();

    const [emojiAberto, setEmojiAberto] = useState(false);
    const [aExcluir, setAExcluir] = useState(false);
    const [apagando, setApagando] = useState(false);

    /** O nome e o logo que o cliente vê no topo da conversa, no celular da prévia. */
    const empresa = useEmpresaDaFolha();

    /** `null` = ainda conferindo. Informação de apoio: nunca derruba a página. */
    const [campanhas, setCampanhas] = useState<Campaign[] | null>(null);

    /** Nome do template de origem, quando a página veio do Duplicar. */
    const [copiadoDe, setCopiadoDe] = useState<string | null>(null);
    /** Id do template de origem cuja imagem do cabeçalho a cópia reaproveita:
     *  a API sobe à Meta o arquivo guardado dele, sem a pessoa reenviar. */
    const [midiaDoOriginal, setMidiaDoOriginal] = useState<number | null>(null);

    const [secaoAtiva, setSecaoAtiva] = useState<SecaoId>('nome');
    const refs = useRef<Record<SecaoId, HTMLElement | null>>({
        nome: null, cabecalho: null, corpo: null, rodape: null, botoes: null,
    });
    const emojiRef = useRef<HTMLDivElement | null>(null);
    const corpoRef = useRef<HTMLTextAreaElement | null>(null);

    /* Ir de um template para `novo?de=…` (o Duplicar) fica na MESMA rota
       `campaign-templates/:id`, então o React não remonta a página: sem este
       reset a tela continuaria em leitura, com a ficha do template de origem
       na tela e o formulário da cópia invisível. É o reset no corpo do render,
       antes dos efeitos — eles rodam depois e preenchem o que for o caso. */
    const [idNaTela, setIdNaTela] = useState(id);
    if (id !== idNaTela) {
        setIdNaTela(id);
        setTemplate(null);
        setCampanhas(null);
        setCopiadoDe(null);
        setModo(novo ? 'editar' : 'ver');
        setCarregando(!novo);
        setForm(VAZIO);
        setExemplos({});
        setBotoes([]);
        setErros({});
        setUpload('idle');
        setArquivoNome('');
        setPreviaLocal(undefined);
        setSecaoAtiva('nome');
    }

    /* ─── Carga ───────────────────────────────────────────────── */

    /** `copia` é o Duplicar: o conteúdo vem igual, mas o template é outro — o
     *  nome ganha sufixo (a Meta recusa nome repetido) e a mídia do cabeçalho
     *  vem do arquivo que a API guardou do template de origem. */
    const hidratar = useCallback((t: MetaTemplate, copia = false) => {
        const v = lerComponentes(t.componentesJson, t.caminhoMidiaHeader);
        setForm({
            nome: copia ? paraSlug(`${t.nome}_copia`) : t.nome,
            idioma: t.idioma || 'pt_BR',
            formatoHeader: v.formatoHeader,
            textoHeader: v.textoHeader ?? '',
            exemploHeaderTexto: '',
            exemploHeaderHandle: '',
            caminhoMidiaHeader: '',
            textoBody: v.textoBody,
            textoFooter: v.textoFooter ?? '',
        });

        /* Os exemplos vêm na ORDEM em que as variáveis aparecem no corpo, não
           indexados pelo número — por isso a leitura casa uma lista com a
           outra em vez de confiar no índice. */
        const ordem: string[] = [];
        for (const m of v.textoBody.matchAll(/\{\{(\d+)\}\}/g)) {
            if (!ordem.includes(m[1])) ordem.push(m[1]);
        }
        const mapa: Record<string, string> = {};
        ordem.forEach((num, i) => { mapa[num] = v.exemplos?.[i] ?? ''; });
        setExemplos(mapa);

        setBotoes(lerBotoes(t.componentesJson));

        /* A mídia já está guardada pela API, que a sobe de novo à Meta ao
           salvar — na edição e na cópia. Cópia de template sem arquivo
           guardado volta a zero: sem arquivo, a Meta recusa o envio. */
        if (v.formatoHeader && v.formatoHeader !== 'TEXT' && (!copia || t.caminhoMidiaHeader)) {
            setUpload('done');
            setArquivoNome(copia ? 'Mesma mídia do template original' : 'Mídia já enviada à Meta');
            if (copia) setMidiaDoOriginal(t.metaTemplateId);
            // A imagem que a pessoa anexou, guardada pela API: sem ela a
            // prévia mostrava só o ícone "Imagem" ao reabrir o template.
            if (v.formatoHeader === 'IMAGE') setPreviaLocal(v.midiaUrl);
        }
    }, []);

    // Criar sem a chave: não há o que mostrar, volta para a lista.
    useEffect(() => {
        if (novo && !podeGerenciar) navegar('/campaign-templates', { replace: true });
    }, [novo, podeGerenciar, navegar]);

    useEffect(() => {
        if (novo || !id) return;
        let vivo = true;
        (async () => {
            try {
                const res = await MetaTemplateService.getById(Number(id));
                if (!vivo) return;
                if (res.sucesso && res.dados) {
                    setTemplate(res.dados);
                    hidratar(res.dados);
                    /* O `?editar=1` só vale se a Meta deixar alterar: nos
                       outros cinco status a página continua em leitura, com
                       a faixa explicando. Pedido dele não pode virar promessa
                       que a Meta recusa. */
                    if (abrirEditando && podeGerenciar && EDITAVEL.includes(res.dados.status)) setModo('editar');
                } else {
                    aviso.erro(res.mensagem || 'Template não encontrado.');
                    navegar('/campaign-templates');
                }
            } catch {
                if (vivo) {
                    aviso.erro(SEM_SERVIDOR);
                    navegar('/campaign-templates');
                }
            } finally {
                if (vivo) setCarregando(false);
            }
        })();
        return () => { vivo = false; };
    }, [id, novo, abrirEditando, podeGerenciar, hidratar, navegar]);

    /* A carga do Duplicar. Usa o mesmo `hidratar` da edição, em modo cópia: o
       formulário continua sendo de um template NOVO (sem `template`, sem id),
       só nasce preenchido. */
    useEffect(() => {
        if (!novo || !copiarDe || !podeGerenciar) return;
        let vivo = true;
        (async () => {
            try {
                const res = await MetaTemplateService.getById(Number(copiarDe));
                if (!vivo) return;
                if (res.sucesso && res.dados) {
                    hidratar(res.dados, true);
                    setCopiadoDe(res.dados.nome);
                } else {
                    /* Origem sumiu: o formulário continua aberto em branco, que
                       é o que a pessoa pediu ao clicar em Novo template. */
                    aviso.erro(res.mensagem || 'Não encontrei o template para copiar.');
                }
            } catch {
                if (vivo) aviso.erro(SEM_SERVIDOR);
            }
        })();
        return () => { vivo = false; };
    }, [novo, copiarDe, podeGerenciar, hidratar]);

    /* Onde este template é usado. A pergunta aparece na hora de excluir — e o
       diálogo já dizia que campanhas param de disparar, sem poder mostrar
       quais. Não exige endpoint novo: `BuscarTodos` de campanha já devolve o
       nome do template de cada uma. */
    useEffect(() => {
        if (novo || !template) return;
        let vivo = true;
        (async () => {
            try {
                const res = await CampaignService.getAll();
                if (vivo) setCampanhas(res.sucesso ? res.dados ?? [] : []);
            } catch {
                // Silencioso: é apoio. A tela do template não depende disto.
                if (vivo) setCampanhas([]);
            }
        })();
        return () => { vivo = false; };
    }, [novo, template]);

    /* As variáveis do corpo mandam na lista de exemplos: aparece uma nova,
       nasce um campo; sai do texto, o campo vai com ela. */
    useEffect(() => {
        const achadas = new Set<string>();
        for (const m of form.textoBody.matchAll(/\{\{(\d+)\}\}/g)) achadas.add(m[1]);
        setExemplos(prev => {
            const proximo: Record<string, string> = {};
            achadas.forEach(n => { proximo[n] = prev[n] ?? ''; });
            const igual = Object.keys(proximo).length === Object.keys(prev).length
                && Object.keys(proximo).every(k => prev[k] === proximo[k]);
            return igual ? prev : proximo;
        });
    }, [form.textoBody]);

    useEffect(() => {
        if (!emojiAberto) return;
        const fora = (e: MouseEvent) => {
            if (emojiRef.current && !emojiRef.current.contains(e.target as Node)) setEmojiAberto(false);
        };
        document.addEventListener('mousedown', fora);
        return () => document.removeEventListener('mousedown', fora);
    }, [emojiAberto]);

    /* ─── Derivados ───────────────────────────────────────────── */

    const numeros = useMemo(
        () => Object.keys(exemplos).sort((a, b) => Number(a) - Number(b)), [exemplos]);

    const ehMidia = !!form.formatoHeader && form.formatoHeader !== 'TEXT' && form.formatoHeader !== 'LOCATION';
    const ehLocal = form.formatoHeader === 'LOCATION';
    const headerComVar = form.formatoHeader === 'TEXT' && form.textoHeader.includes('{{1}}');

    const contagem = useMemo(() => {
        const conta = (tipo: TipoBotao) => botoes.filter(b => b.tipo === tipo).length;
        return {
            url: conta('URL'), telefone: conta('PHONE_NUMBER'), voz: conta('VOICE_CALL'),
            codigo: conta('COPY_CODE'), flow: conta('FLOW'),
        };
    }, [botoes]);

    const visual: TemplateVisual = useMemo(() => ({
        formatoHeader: form.formatoHeader,
        textoHeader: form.textoHeader,
        textoBody: form.textoBody,
        textoFooter: form.textoFooter,
        botoes: botoes.map(b => ({ tipo: b.tipo, texto: b.texto })),
        exemplos: numeros.map(n => exemplos[n]),
        midiaUrl: previaLocal,
    }), [form, botoes, exemplos, numeros, previaLocal]);

    /* As regras da Meta que dá para conferir aqui, antes de gastar uma
       análise. Elas AVISAM — a autoridade é a Meta, não esta lista. */
    const checagens = useMemo(() => {
        const corpo = form.textoBody.trim();
        /* `espera` é o estado de quem ainda não tem o que conferir. Sem ele a
           caixa abre reprovando um formulário em branco — corpo e nome vazios
           caíam como erro antes de a pessoa digitar a primeira letra. */
        const lista: Checagem[] = [
            {
                espera: !corpo ? 'Escreva o corpo da mensagem.' : null,
                ok: !!corpo && !/^\s*\{\{\d+\}\}/.test(corpo),
                texto: 'O corpo não começa com variável',
                detalhe: 'A Meta recusa (INVALID_FORMAT) quando a mensagem abre com {{1}}.',
            },
            {
                espera: !corpo ? 'As variáveis do corpo entram aqui.' : null,
                ok: numeros.length === 0 || numeros.every(n => !!exemplos[n]?.trim()),
                texto: numeros.length === 0
                    ? 'Nenhuma variável para exemplificar'
                    : `${numeros.length === 1 ? 'A variável tem' : `As ${numeros.length} variáveis têm`} exemplo`,
                detalhe: 'Sem exemplo a Meta não consegue avaliar a mensagem.',
            },
            {
                espera: !form.textoFooter.trim() ? 'Sem rodapé — ele é opcional.' : null,
                ok: !/\{\{\d+\}\}/.test(form.textoFooter),
                texto: 'Rodapé sem variável',
                detalhe: 'A Meta não aceita variável no rodapé.',
            },
            {
                espera: !form.nome.trim() ? 'Dê um nome ao template.' : null,
                ok: form.nome === paraSlug(form.nome),
                texto: 'Nome em minúsculas, sem espaço',
                detalhe: 'O nome é convertido automaticamente.',
            },
        ];
        if (ehMidia) {
            lista.push({
                espera: null,
                ok: upload === 'done',
                texto: 'Mídia do cabeçalho enviada',
                detalhe: 'A Meta precisa de um arquivo de exemplo para aprovar o cabeçalho.',
            });
        }
        return lista;
    }, [form, numeros, exemplos, ehMidia, upload]);

    const podeEditar = novo || (template && EDITAVEL.includes(template.status));
    const editando = modo === 'editar';

    /* O par que identifica um template na Meta é nome + idioma, então o
       vínculo da campanha se confere pelos dois. */
    const usos = useMemo(() => (campanhas ?? []).filter(c =>
        c.campanhaTemplateNome === template?.nome
        && (!c.campanhaTemplateIdioma || c.campanhaTemplateIdioma === template?.idioma)),
        [campanhas, template]);

    /* ─── Ações ───────────────────────────────────────────────── */

    const irPara = (s: SecaoId) => {
        setSecaoAtiva(s);
        refs.current[s]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const trocarFormato = (valor: string) => {
        const f = valor === 'NONE' ? undefined : (valor as FormatoHeader);
        setForm(prev => ({
            ...prev, formatoHeader: f, textoHeader: '', exemploHeaderTexto: '',
            exemploHeaderHandle: '', caminhoMidiaHeader: '',
        }));
        setPreviaLocal(undefined);
        setUpload('idle');
        setArquivoNome('');
        setMidiaDoOriginal(null);
        setErros(e => ({ ...e, midia: '' }));
    };

    const enviarMidia = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const arquivo = e.target.files?.[0];
        if (!arquivo) return;

        if (form.formatoHeader === 'IMAGE') {
            const leitor = new FileReader();
            leitor.onload = ev => setPreviaLocal(ev.target?.result as string);
            leitor.readAsDataURL(arquivo);
        } else {
            setPreviaLocal(undefined);
        }

        setArquivoNome(arquivo.name);
        setUpload('uploading');
        setErros(x => ({ ...x, midia: '' }));

        try {
            const res = await MetaTemplateService.obterHandleExemplo(arquivo);
            if (res.sucesso && res.dados) {
                setForm(prev => ({
                    ...prev,
                    exemploHeaderHandle: res.dados.handle,
                    caminhoMidiaHeader: res.dados.caminhoLocal,
                }));
                setUpload('done');
            } else {
                setUpload('error');
                setPreviaLocal(undefined);
                setErros(x => ({ ...x, midia: res.mensagem || 'A Meta não aceitou este arquivo.' }));
            }
        } catch {
            setUpload('error');
            setPreviaLocal(undefined);
            setErros(x => ({ ...x, midia: SEM_SERVIDOR }));
        }
    };

    const limparMidia = () => {
        setPreviaLocal(undefined);
        setUpload('idle');
        setArquivoNome('');
        setMidiaDoOriginal(null);
        setForm(prev => ({ ...prev, exemploHeaderHandle: '', caminhoMidiaHeader: '' }));
    };

    const addBotao = () => {
        if (botoes.length >= MAX_BOTOES) return;
        setBotoes(prev => [...prev, { tipo: 'QUICK_REPLY', texto: '' }]);
    };

    const removeBotao = (i: number) => setBotoes(prev => prev.filter((_, x) => x !== i));

    const mudarBotao = (i: number, campo: keyof BotaoTemplate, valor: string) => {
        setBotoes(prev => prev.map((b, x) => {
            if (x !== i) return b;
            /* Trocar o tipo limpa os campos do tipo antigo e mantém o texto:
               é o rótulo que a pessoa escreveu, não muda de sentido. */
            if (campo === 'tipo') return { tipo: valor as TipoBotao, texto: b.texto };
            return { ...b, [campo]: valor };
        }));
    };

    const opcoesTipo = (atual: TipoBotao) => TIPOS_BOTAO.map(t => {
        const { url, telefone, voz, codigo, flow } = contagem;
        const jaEh = t.value === atual;
        let bloqueio = '';
        if (!jaEh) {
            if (t.value === 'URL' && url >= 2) bloqueio = 'máx. 2';
            if (t.value === 'PHONE_NUMBER' && voz >= 1) bloqueio = 'conflita com chamada de voz';
            else if (t.value === 'PHONE_NUMBER' && telefone >= 1) bloqueio = 'máx. 1';
            if (t.value === 'VOICE_CALL' && telefone >= 1) bloqueio = 'conflita com telefone';
            else if (t.value === 'VOICE_CALL' && voz >= 1) bloqueio = 'máx. 1';
            if (t.value === 'COPY_CODE' && codigo >= 1) bloqueio = 'máx. 1';
            if (t.value === 'FLOW' && flow >= 1) bloqueio = 'máx. 1';
        }
        return {
            value: t.value,
            label: bloqueio ? `${t.label} (${bloqueio})` : t.label,
            disabled: !!bloqueio,
        };
    });

    /* Insere no cursor do corpo. O textarea guarda a seleção mesmo depois de
       perder o foco para o botão; sem cursor conhecido, vai para o fim. */
    const inserirNoCorpo = (trecho: string) => {
        const ta = corpoRef.current;
        const texto = form.textoBody;
        const inicio = ta ? ta.selectionStart : texto.length;
        const fim = ta ? ta.selectionEnd : texto.length;
        const novo = texto.slice(0, inicio) + trecho + texto.slice(fim);
        if (novo.length > MAX_CORPO) return;
        setForm(p => ({ ...p, textoBody: novo }));
        requestAnimationFrame(() => {
            if (!corpoRef.current) return;
            const pos = inicio + trecho.length;
            corpoRef.current.focus();
            corpoRef.current.setSelectionRange(pos, pos);
        });
    };

    const inserirEmoji = (dado: EmojiClickData) => inserirNoCorpo(dado.emoji);

    const validar = (): Record<string, string> => {
        const e: Record<string, string> = {};
        if (!form.nome.trim()) e.nome = 'O template precisa de um nome.';
        if (!form.textoBody.trim()) e.corpo = 'O corpo da mensagem é obrigatório.';
        if (form.textoBody.length > MAX_CORPO) e.corpo = `O corpo passa de ${MAX_CORPO} caracteres.`;
        numeros.forEach(n => {
            if (!exemplos[n]?.trim()) e[`ex${n}`] = 'A Meta exige um exemplo.';
        });
        if (headerComVar && !form.exemploHeaderTexto.trim()) {
            e.exemploHeader = 'O cabeçalho tem {{1}} e precisa de exemplo.';
        }
        if (ehMidia && upload !== 'done') e.midia = 'Envie o arquivo de exemplo do cabeçalho.';
        botoes.forEach((b, i) => {
            if (!b.texto.trim()) e[`bt${i}`] = 'O botão precisa de um texto.';
            if (b.tipo === 'URL' && !b.url?.trim()) e[`bt${i}`] = 'Informe o endereço do link.';
            if (b.tipo === 'PHONE_NUMBER' && !b.telefone?.trim()) e[`bt${i}`] = 'Informe o telefone.';
            if (b.tipo === 'COPY_CODE' && !b.codigoOferta?.trim()) e[`bt${i}`] = 'Informe o código.';
            if (b.tipo === 'FLOW' && !b.flow_id?.trim()) e[`bt${i}`] = 'Informe o Flow ID.';
        });
        return e;
    };

    const salvar = async () => {
        const problemas = validar();
        setErros(problemas);
        if (Object.keys(problemas).length > 0) {
            /* Erro de validação mora no campo, não em toast. O que o toast
               faria — chamar atenção — aqui é levar a pessoa até lá. */
            const primeira: SecaoId = problemas.nome ? 'nome'
                : problemas.midia || problemas.exemploHeader ? 'cabecalho'
                    : problemas.corpo || numeros.some(n => problemas[`ex${n}`]) ? 'corpo'
                        : 'botoes';
            irPara(primeira);
            return;
        }

        const corpoExemplos = numeros.map(n => exemplos[n] || '');
        const payload: CreateTemplateDTO = {
            nome: paraSlug(form.nome),
            /* A categoria não é campo da tela: hoje tudo é Marketing. Ao
               EDITAR, manda a do próprio template em vez da constante — senão
               um template de Utilidade que veio do Sincronizar viraria
               Marketing sem avisar, e isso muda o preço do disparo. */
            categoria: template?.categoria ?? 'MARKETING',
            idioma: form.idioma,
            textoBody: form.textoBody,
            textoHeader: form.formatoHeader === 'TEXT' ? form.textoHeader.trim() || undefined : undefined,
            formatoHeader: form.formatoHeader,
            exemploHeaderHandle: ehMidia ? form.exemploHeaderHandle || undefined : undefined,
            caminhoMidiaHeader: ehMidia ? form.caminhoMidiaHeader || undefined : undefined,
            copiarMidiaDoTemplateId: ehMidia && !form.exemploHeaderHandle ? midiaDoOriginal ?? undefined : undefined,
            exemploHeaderTexto: headerComVar ? form.exemploHeaderTexto.trim() || undefined : undefined,
            textoFooter: form.textoFooter.trim() || undefined,
            botoes: botoes.length > 0 ? botoes : undefined,
            exemplosBody: corpoExemplos.length > 0 ? corpoExemplos : undefined,
        };

        setSalvando(true);
        try {
            const res = template
                ? await MetaTemplateService.update({
                    metaTemplateId: template.metaTemplateId,
                    categoria: template.categoria,
                    textoBody: payload.textoBody,
                    textoHeader: payload.textoHeader,
                    formatoHeader: payload.formatoHeader,
                    exemploHeaderHandle: payload.exemploHeaderHandle,
                    exemploHeaderTexto: payload.exemploHeaderTexto,
                    caminhoMidiaHeader: payload.caminhoMidiaHeader,
                    textoFooter: payload.textoFooter,
                    botoes: payload.botoes,
                    exemplosBody: payload.exemplosBody,
                })
                : await MetaTemplateService.create(payload);

            if (res.sucesso) {
                aviso.ok(template
                    ? 'Alteração enviada — a Meta vai analisar de novo.'
                    : 'Template criado e enviado para análise da Meta.');
                navegar('/campaign-templates');
            } else {
                aviso.erro(res.mensagem || 'A Meta não aceitou o template.');
            }
        } catch {
            aviso.erro(SEM_SERVIDOR);
        } finally {
            setSalvando(false);
        }
    };

    const excluir = async () => {
        if (!template) return;
        setApagando(true);
        try {
            const res = await MetaTemplateService.remove(template.metaTemplateId);
            if (res.sucesso) {
                aviso.ok(`Template "${template.nome}" excluído na Meta.`);
                navegar('/campaign-templates');
            } else {
                aviso.erro(res.mensagem || 'A Meta não deixou excluir este template.');
                setAExcluir(false);
            }
        } catch {
            aviso.erro(SEM_SERVIDOR);
            setAExcluir(false);
        } finally {
            setApagando(false);
        }
    };

    /* ─── Cabeçalho da página ─────────────────────────────────── */

    const voltar = (
        <button type="button" className="tpl-back" onClick={() => navegar('/campaign-templates')}>
            <LuChevronLeft size={14} /> Templates
        </button>
    );

    const apoio = novo
        ? 'A Meta analisa o template antes de ele poder ser disparado.'
        : template
            ? `${STATUS_LABEL[template.status]} na Meta · ${rotuloIdioma(template.idioma)} · sincronizado ${desdeAgora(template.sincronizadoEm)}`
            : 'Carregando…';

    const acoes = editando ? (
        <div className="tpl-tools">
            <button type="button" className="tpl-btn" disabled={salvando}
                onClick={() => (novo ? navegar('/campaign-templates') : setModo('ver'))}>
                Descartar
            </button>
            <button type="button" className="tpl-btn is-primary" onClick={salvar} disabled={salvando}>
                {salvando ? 'Enviando…' : novo ? 'Criar e enviar para análise' : 'Salvar e enviar para análise'}
            </button>
        </div>
    ) : podeGerenciar ? (
        <div className="tpl-tools">
            <button type="button" className="tpl-btn"
                onClick={() => navegar(`/campaign-templates/novo?de=${template?.metaTemplateId}`)}>
                <LuCopy size={14} /> Duplicar
            </button>
            {podeEditar && (
                <button type="button" className="tpl-btn is-primary" onClick={() => setModo('editar')}>
                    <LuPencil size={14} />
                    {template?.status === 'REJECTED' ? 'Corrigir e reenviar' : 'Editar'}
                </button>
            )}
        </div>
    ) : undefined;

    /* ─── Trilho ──────────────────────────────────────────────── */

    /* O trilho só existe em EDIÇÃO. Em leitura o conteúdo é um resumo que
       cabe de uma vez, e um "Ir para" que não rola nada é um controle que
       promete e não cumpre — foi o que ele apontou em 18/09. */
    const trilho = (
        <nav className="tpl-rail" aria-label="Partes do template">
            <span className="tpl-rail-t">Ir para</span>
            {/* Sem contador ao lado do rótulo: ele era a contagem de variáveis
                e de botões, e ele perguntou "o que é ele? variável? não ficou
                claro" — número sozinho numa pílula lê como notificação. A
                contagem já aparece onde significa algo: "Exemplos das N
                variáveis detectadas" e "Botões · N de 10". */}
            {SECOES.map(s => {
                const Icone = s.icone;
                return (
                    <button key={s.id} type="button"
                        className={`tpl-rail-i ${secaoAtiva === s.id ? 'is-on' : ''}`}
                        onClick={() => irPara(s.id)}>
                        <Icone size={14} aria-hidden="true" />
                        <span>{s.label}</span>
                    </button>
                );
            })}
            {!novo && template && podeGerenciar && (
                <span className="tpl-rail-ft">
                    <button type="button" className="tpl-rail-i is-danger" onClick={() => setAExcluir(true)}>
                        <LuTrash2 size={14} aria-hidden="true" />
                        <span>Excluir template</span>
                    </button>
                </span>
            )}
        </nav>
    );

    /* Sem trilho, o excluir precisa de casa: vai para o fim da coluna, que
       é longe das ações do cabeçalho. */
    /* ─── Leitura: a ficha (coluna estreita) ──────────────────────
       Mesmo desenho da página de Oportunidade: cartão com filetes entre as
       seções. Sem trilho em leitura, é aqui que o excluir mora — no pé da
       ficha, longe das ações do cabeçalho. */
    const fichaLeitura = template && (
        <aside className="tpl-ficha">
            <section className="tpl-fsec">
                <h3 className="tpl-fsec-t">
                    <LuIdCard size={12} aria-hidden="true" /> Identidade
                </h3>
                <dl className="tpl-kv">
                    <dt>Nome</dt><dd className="is-mono">{template.nome}</dd>
                    <dt>Idioma</dt><dd>{rotuloIdioma(template.idioma)}</dd>
                </dl>
            </section>

            <section className="tpl-fsec">
                <h3 className="tpl-fsec-t">
                    <LuMegaphone size={12} aria-hidden="true" /> Estado na Meta
                </h3>
                <dl className="tpl-kv">
                    <dt>Status</dt>
                    <dd><span className={`tpl-st ${STATUS_TOM[template.status]}`}>
                        {STATUS_LABEL[template.status]}
                    </span></dd>
                    <dt>Qualidade</dt>
                    <dd>{tomQualidade(template.qualidade) === null
                        ? <span className="tpl-dash">Sem nota</span>
                        : <span className={`tpl-q ${tomQualidade(template.qualidade)}`}>
                            <i />{rotuloQualidade(template.qualidade)}</span>}</dd>
                    <dt>Criado</dt><dd>{dataCurta(template.criadoEm)}</dd>
                    <dt>Sincronizado</dt><dd>{desdeAgora(template.sincronizadoEm)}</dd>
                    <dt>ID na Meta</dt><dd className="is-mono">{template.templateMetaId || '—'}</dd>
                </dl>
                {tomQualidade(template.qualidade) === null && (
                    <span className="tpl-hint">
                        A Meta só pontua a qualidade depois de o template entregar volume.
                    </span>
                )}
            </section>

            {/* "Usado em" mora na ficha desde 28/10: é dado do template, como status e
                qualidade — a coluna larga ficou só para a mensagem. */}
            <section className="tpl-fsec">
                <h3 className="tpl-fsec-t">
                    <LuSend size={12} aria-hidden="true" /> Usado em
                </h3>
                {campanhas === null ? (
                    <span className="tpl-hint">Conferindo as campanhas…</span>
                ) : usos.length === 0 ? (
                    <span className="tpl-hint">
                        Nenhuma campanha usa este template. Excluir não interrompe disparo nenhum.
                    </span>
                ) : (
                    <ul className="tpl-usos">
                        {usos.map(c => (
                            <li key={c.campanhaId}>
                                <span>{c.campanhaNome}</span>
                                <span className="tpl-dash">{c.campanhaStatus}</span>
                            </li>
                        ))}
                    </ul>
                )}
            </section>

            {template.status === 'REJECTED' && !!template.motivoRejeicao && (
                <section className="tpl-fsec">
                    <h3 className="tpl-fsec-t">
                        <LuCircleAlert size={12} aria-hidden="true" /> O que reprovou
                    </h3>
                    <MotivoRejeicao template={template} />
                </section>
            )}

            {/* Histórico (fase 5): criação, resposta da Meta (aprovado, rejeitado e o motivo),
                conteúdo alterado, exclusão. Fica antes do excluir, que é o pé da ficha. */}
            <section className="tpl-fsec">
                <h3 className="tpl-fsec-t">
                    <LuHistory size={12} aria-hidden="true" /> Histórico
                </h3>
                <SecaoHistorico entidade="template" id={template.metaTemplateId} variante="pagina" />
            </section>

            {podeGerenciar && (
                <section className="tpl-fsec">
                    <button type="button" className="tpl-btn is-danger" onClick={() => setAExcluir(true)}>
                        <LuTrash2 size={13} /> Excluir template
                    </button>
                    <span className="tpl-hint">
                        Apaga na Meta, em todos os idiomas, e não tem desfazer.
                    </span>
                </section>
            )}
        </aside>
    );

    /* ─── Coluna do meio ──────────────────────────────────────── */

    const midiaIcone = form.formatoHeader === 'VIDEO' ? <LuVideo size={18} />
        : form.formatoHeader === 'DOCUMENT' ? <LuFileText size={18} />
            : <LuImage size={18} />;

    /* ─── Leitura: a mensagem dentro de um celular ────────────────
       Desenho de 28/10/2026 (template-leitura-e-historico.html). O bloco
       "Como está escrito" saiu: sem variável, o texto era o mesmo da bolha,
       e cabeçalho, rodapé e tipo de botão já aparecem nela (o tipo, pelo
       ícone). O que só ele mostrava, o `{{1}}`, virou a legenda de baixo —
       fora do celular, porque não é parte do que o cliente recebe. */
    const mensagemLeitura = (
        <div>
            <h3 className="tpl-bloco-t">
                <LuMegaphone size={12} aria-hidden="true" /> Como o cliente recebe
            </h3>
            <div className="tpl-cel-palco">
                <CelularWhatsApp nome={empresa.nome || 'Sua empresa'} fotoUrl={empresa.logoUrl}>
                    <TemplateBubble visual={visual} resolver roomy
                        vazio="Este template não tem corpo salvo." />
                </CelularWhatsApp>
                {numeros.length > 0 && (
                    <p className="tpl-cel-nota">
                        {numeros.length === 1 ? 'Exemplo enviado' : 'Exemplos enviados'} à Meta:{' '}
                        {numeros.map((n, i) => (
                            <span key={n}>
                                {i > 0 && ' · '}<code>{`{{${n}}}`}</code> = {exemplos[n] || '—'}
                            </span>
                        ))}. No disparo, cada contato recebe o próprio valor.
                    </p>
                )}
            </div>
        </div>
    );


    const formulario = (
        <>
            <section className="tpl-sec" ref={el => { refs.current.nome = el; }}>
                <h2 className="tpl-sec-t">
                    <LuIdCard size={14} aria-hidden="true" /> Nome e idioma
                </h2>
                <div className="tpl-row2">
                    <div className="tpl-field">
                        <label className="tpl-lab" htmlFor="tpl-nome">
                            Nome do template <span className="tpl-req">*</span>
                        </label>
                        <input
                            id="tpl-nome"
                            type="text"
                            className={`tpl-inp ${erros.nome ? 'has-err' : ''}`}
                            value={form.nome}
                            /* O nome é o identificador na Meta e não muda depois
                               de aprovado — por isso trava na edição. */
                            disabled={!novo}
                            onChange={e => setForm(p => ({ ...p, nome: paraSlug(e.target.value) }))}
                            placeholder="boas_vindas_imovel"
                        />
                        {erros.nome
                            ? <span className="tpl-err"><LuCircleAlert size={12} />{erros.nome}</span>
                            : <span className="tpl-hint">
                                {novo
                                    ? 'Minúsculas, números e underscore. Espaço vira underscore automaticamente.'
                                    : 'O nome não muda depois que a Meta aprova o template.'}
                            </span>}
                    </div>
                    <div className="tpl-field">
                        <label className="tpl-lab" htmlFor="tpl-idioma">
                            Idioma <span className="tpl-req">*</span>
                        </label>
                        <Select id="tpl-idioma" size="md" value={form.idioma} options={IDIOMAS}
                            disabled={!novo} onChange={v => setForm(p => ({ ...p, idioma: v }))} />
                    </div>
                </div>
            </section>

            <section className="tpl-sec" ref={el => { refs.current.cabecalho = el; }}>
                <h2 className="tpl-sec-t">
                    <LuImage size={14} aria-hidden="true" /> Cabeçalho <em>opcional</em>
                </h2>
                <p className="tpl-sec-sub">
                    Uma linha de texto, uma imagem, um vídeo, um documento ou a localização — o que
                    aparece acima da mensagem.
                </p>

                <div className="tpl-field tpl-w-sel">
                    <label className="tpl-lab" htmlFor="tpl-formato">Tipo</label>
                    <Select id="tpl-formato" size="md" value={form.formatoHeader ?? 'NONE'}
                        options={FORMATOS} onChange={trocarFormato} />
                </div>

                {form.formatoHeader === 'TEXT' && (
                    <>
                        <div className="tpl-field">
                            <label className="tpl-lab" htmlFor="tpl-header">Texto do cabeçalho</label>
                            <input id="tpl-header" type="text" className="tpl-inp" maxLength={60}
                                value={form.textoHeader}
                                onChange={e => setForm(p => ({ ...p, textoHeader: e.target.value }))}
                                placeholder="Sua visita está confirmada" />
                            <span className="tpl-hint">Até 60 caracteres. Aceita uma variável, {'{{1}}'}.</span>
                        </div>
                        {headerComVar && (
                            <div className="tpl-field">
                                <label className="tpl-lab" htmlFor="tpl-exheader">
                                    Exemplo do {'{{1}}'} do cabeçalho <span className="tpl-req">*</span>
                                </label>
                                <input id="tpl-exheader" type="text"
                                    className={`tpl-inp ${erros.exemploHeader ? 'has-err' : ''}`}
                                    value={form.exemploHeaderTexto}
                                    onChange={e => setForm(p => ({ ...p, exemploHeaderTexto: e.target.value }))}
                                    placeholder="Jardim Europa" />
                                {erros.exemploHeader && (
                                    <span className="tpl-err"><LuCircleAlert size={12} />{erros.exemploHeader}</span>
                                )}
                            </div>
                        )}
                    </>
                )}

                {ehLocal && (
                    <p className="tpl-faixa is-wait">
                        <LuMapPin size={14} aria-hidden="true" />
                        <span>
                            <b>A localização vai no disparo, não aqui.</b> O template é aprovado com o
                            campo vazio e cada envio manda as coordenadas.
                        </span>
                    </p>
                )}

                {ehMidia && (
                    <div className="tpl-field">
                        <label className="tpl-lab">
                            Arquivo de exemplo <span className="tpl-req">*</span>
                        </label>

                        {upload === 'done' ? (
                            <div className="tpl-up">
                                <span className="tpl-thumb">
                                    {previaLocal ? <img src={previaLocal} alt="" /> : midiaIcone}
                                </span>
                                <span className="tpl-up-txt">
                                    <span className="tpl-up-ok">
                                        <LuCircleCheck size={13} aria-hidden="true" /> {arquivoNome}
                                    </span>
                                    <span className="tpl-hint">
                                        A Meta guarda um <i>handle</i>, não o arquivo — ele é reusado no disparo.
                                    </span>
                                </span>
                                <button type="button" className="tpl-act" onClick={limparMidia}
                                    aria-label="Trocar arquivo" title="Trocar arquivo">
                                    <LuX size={14} />
                                </button>
                            </div>
                        ) : (
                            <label className={`tpl-up ${erros.midia ? 'has-err' : ''}`}>
                                <span className="tpl-thumb">
                                    {upload === 'uploading' ? <span className="tpl-spin" /> : <LuUpload size={16} />}
                                </span>
                                <span className="tpl-up-txt">
                                    {upload === 'uploading'
                                        ? <b>Enviando {arquivoNome}…</b>
                                        : <><b>Escolher arquivo</b>
                                            <span className="tpl-hint">
                                                {form.formatoHeader === 'IMAGE' && 'JPG ou PNG.'}
                                                {form.formatoHeader === 'VIDEO' && 'MP4 ou 3GPP.'}
                                                {form.formatoHeader === 'DOCUMENT' && 'PDF, DOC, DOCX, PPTX ou XLSX.'}
                                            </span>
                                        </>}
                                </span>
                                <input type="file" disabled={upload === 'uploading'}
                                    accept={MEDIA_ACCEPT[form.formatoHeader ?? ''] ?? undefined}
                                    onChange={enviarMidia} />
                            </label>
                        )}

                        {erros.midia && (
                            <span className="tpl-err"><LuCircleAlert size={12} />{erros.midia}</span>
                        )}
                    </div>
                )}
            </section>

            <section className="tpl-sec" ref={el => { refs.current.corpo = el; }}>
                <h2 className="tpl-sec-t">
                    <LuTextQuote size={14} aria-hidden="true" /> Corpo <span className="tpl-req">*</span>
                </h2>
                <p className="tpl-sec-sub">
                    O texto da mensagem. Use {'{{1}}'}, {'{{2}}'} para o que muda a cada envio — cada
                    variável precisa de um exemplo, que é o que a Meta analisa.
                </p>

                <div className="tpl-field">
                    <div className="tpl-ta-wrap">
                        <textarea
                            id="tpl-corpo"
                            ref={corpoRef}
                            className={`tpl-ta ${erros.corpo ? 'has-err' : ''}`}
                            value={form.textoBody}
                            maxLength={MAX_CORPO}
                            rows={6}
                            onChange={e => setForm(p => ({ ...p, textoBody: e.target.value }))}
                            placeholder="Olá {{1}}! Recebemos seu interesse no {{2}}."
                        />
                        <span className="tpl-cnt">{form.textoBody.length} / {MAX_CORPO}</span>
                    </div>
                    {erros.corpo && (
                        <span className="tpl-err"><LuCircleAlert size={12} />{erros.corpo}</span>
                    )}

                    <div className="tpl-tools">
                        <button type="button" className="tpl-btn is-sm"
                            onClick={() => {
                                const proxima = (numeros.length
                                    ? Math.max(...numeros.map(Number)) + 1 : 1);
                                inserirNoCorpo(`{{${proxima}}}`);
                            }}>
                            <LuBraces size={13} /> Adicionar variável
                        </button>
                        <div ref={emojiRef} style={{ position: 'relative' }}>
                            <button type="button" className="tpl-btn is-sm"
                                onClick={() => setEmojiAberto(v => !v)} aria-expanded={emojiAberto}>
                                <LuSmile size={13} /> Emoji
                            </button>
                            {emojiAberto && (
                                <div style={{ position: 'absolute', zIndex: 60, top: 'calc(100% + 6px)', left: 0 }}>
                                    <EmojiPicker
                                        onEmojiClick={inserirEmoji}
                                        theme={theme === 'dark' ? Theme.DARK : Theme.LIGHT}
                                        searchPlaceHolder="Buscar emoji"
                                        width={320}
                                        height={360}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {numeros.length > 0 && (
                    <div className="tpl-vars">
                        <div className="tpl-vars-t">
                            {numeros.length === 1
                                ? 'Exemplo da variável detectada'
                                : `Exemplos das ${numeros.length} variáveis detectadas`}
                        </div>
                        {numeros.map(n => (
                            <div key={n}>
                                <div className="tpl-vars-r">
                                    <span className="tpl-vars-k">{`{{${n}}}`}</span>
                                    <input type="text"
                                        className={`tpl-inp ${erros[`ex${n}`] ? 'has-err' : ''}`}
                                        value={exemplos[n] ?? ''}
                                        onChange={e => setExemplos(p => ({ ...p, [n]: e.target.value }))}
                                        placeholder={`o que entra no lugar de {{${n}}}`}
                                        aria-label={`Exemplo da variável ${n}`} />
                                </div>
                                {erros[`ex${n}`] && (
                                    <span className="tpl-err"><LuCircleAlert size={12} />{erros[`ex${n}`]}</span>
                                )}
                            </div>
                        ))}
                        <span className="tpl-hint">
                            A Meta usa os exemplos na aprovação; o telefone ao lado mostra a mensagem
                            já preenchida com eles.
                        </span>
                    </div>
                )}
            </section>

            <section className="tpl-sec" ref={el => { refs.current.rodape = el; }}>
                <h2 className="tpl-sec-t">
                    <LuTag size={14} aria-hidden="true" /> Rodapé <em>opcional</em>
                </h2>
                <div className="tpl-field">
                    <input type="text" className="tpl-inp" maxLength={MAX_RODAPE}
                        value={form.textoFooter}
                        onChange={e => setForm(p => ({ ...p, textoFooter: e.target.value }))}
                        placeholder="Brik Imóveis" aria-label="Rodapé" />
                    <span className="tpl-hint">
                        Até {MAX_RODAPE} caracteres, sem variável. Costuma levar o nome da empresa.
                    </span>
                </div>
            </section>

            <section className="tpl-sec" ref={el => { refs.current.botoes = el; }}>
                <h2 className="tpl-sec-t">
                    <LuReply size={14} aria-hidden="true" /> Botões
                    <em>opcional · {botoes.length} de {MAX_BOTOES}</em>
                </h2>
                <p className="tpl-sec-sub">
                    O que o cliente pode tocar na mensagem. A Meta limita por tipo: <b>2 links</b>,
                    {' '}<b>1 telefone</b> (que conflita com chamada de voz), <b>1 copiar código</b> e
                    {' '}<b>1 Flow</b>.
                </p>

                {botoes.map((b, i) => (
                    <div key={i}>
                        <div className="tpl-btl">
                            <Select size="md" value={b.tipo} options={opcoesTipo(b.tipo)}
                                onChange={v => mudarBotao(i, 'tipo', v)}
                                ariaLabel={`Tipo do botão ${i + 1}`} />
                            <input type="text" className={`tpl-inp ${erros[`bt${i}`] ? 'has-err' : ''}`}
                                value={b.texto} maxLength={25}
                                onChange={e => mudarBotao(i, 'texto', e.target.value)}
                                placeholder="Texto do botão"
                                aria-label={`Texto do botão ${i + 1}`} />
                            <button type="button" className="tpl-act is-danger"
                                onClick={() => removeBotao(i)}
                                aria-label={`Remover botão ${i + 1}`} title="Remover botão">
                                <LuTrash2 size={13} />
                            </button>
                        </div>

                        {b.tipo === 'URL' && (
                            <div className="tpl-btl-extra">
                                <input type="text" className="tpl-inp" value={b.url ?? ''}
                                    onChange={e => mudarBotao(i, 'url', e.target.value)}
                                    placeholder="https://..." aria-label="Endereço do link" />
                            </div>
                        )}
                        {b.tipo === 'PHONE_NUMBER' && (
                            <div className="tpl-btl-extra">
                                <input type="text" className="tpl-inp" value={b.telefone ?? ''}
                                    onChange={e => mudarBotao(i, 'telefone', e.target.value)}
                                    placeholder="+5511999999999" aria-label="Telefone" />
                            </div>
                        )}
                        {b.tipo === 'COPY_CODE' && (
                            <div className="tpl-btl-extra">
                                <input type="text" className="tpl-inp" value={b.codigoOferta ?? ''}
                                    onChange={e => mudarBotao(i, 'codigoOferta', e.target.value)}
                                    placeholder="PROMO20" aria-label="Código da oferta" />
                            </div>
                        )}
                        {b.tipo === 'FLOW' && (
                            <div className={`tpl-btl-extra ${b.flow_action === 'NAVIGATE' ? 'is-dupla' : ''}`}>
                                <input type="text" className="tpl-inp" value={b.flow_id ?? ''}
                                    onChange={e => mudarBotao(i, 'flow_id', e.target.value)}
                                    placeholder="Flow ID" aria-label="Flow ID" />
                                <Select size="md" value={b.flow_action ?? 'NAVIGATE'}
                                    options={[
                                        { value: 'NAVIGATE', label: 'Navigate' },
                                        { value: 'DATA_EXCHANGE', label: 'Data exchange' },
                                    ]}
                                    onChange={v => mudarBotao(i, 'flow_action', v)}
                                    ariaLabel="Ação do Flow" />
                                {b.flow_action === 'NAVIGATE' && (
                                    <input type="text" className="tpl-inp" value={b.navigate_screen ?? ''}
                                        onChange={e => mudarBotao(i, 'navigate_screen', e.target.value)}
                                        placeholder="Nome da tela (opcional)"
                                        aria-label="Tela do Flow" />
                                )}
                            </div>
                        )}

                        {erros[`bt${i}`] && (
                            <span className="tpl-err" style={{ marginBottom: 10 }}>
                                <LuCircleAlert size={12} />{erros[`bt${i}`]}
                            </span>
                        )}
                    </div>
                ))}

                {botoes.length < MAX_BOTOES && (
                    <div className="tpl-tools">
                        <button type="button" className="tpl-btn is-sm" onClick={addBotao}>
                            <LuPlus size={13} /> Adicionar botão
                        </button>
                    </div>
                )}
            </section>
        </>
    );

    /* ─── Coluna da direita ───────────────────────────────────── */

    const direita = (
        <aside className="tpl-pv">
            {/* O mesmo celular da leitura, na largura da coluna: quem edita vê a mensagem
                como vai vê-la depois de salvar. */}
            <h3 className="tpl-bloco-t">
                <LuMegaphone size={12} aria-hidden="true" />
                {editando ? 'Pré-visualização' : 'Como foi enviado'}
            </h3>
            <CelularWhatsApp nome={empresa.nome || 'Sua empresa'} fotoUrl={empresa.logoUrl} compacto>
                <TemplateBubble visual={visual} resolver roomy
                    vazio="Escreva o corpo da mensagem para ver a prévia." />
            </CelularWhatsApp>

            {editando && (
                <div className="tpl-box">
                    <div className="tpl-box-t">Antes de enviar</div>
                    {checagens.map((c, i) => (
                        <div key={i}
                            className={`tpl-ck ${c.espera ? 'is-espera' : c.ok ? '' : 'is-bad'}`}>
                            {c.espera
                                ? <LuCircleDashed size={13} />
                                : c.ok ? <LuCircleCheck size={13} /> : <LuCircleX size={13} />}
                            <span>{c.espera ?? (c.ok ? c.texto : c.detalhe)}</span>
                        </div>
                    ))}
                </div>
            )}

            {template && (
                <div className="tpl-box">
                    <div className="tpl-box-t">Estado na Meta</div>
                    <dl style={{ margin: 0 }}>
                        <div className="tpl-box-r">
                            <dt>Status</dt>
                            <dd><span className={`tpl-st ${STATUS_TOM[template.status]}`}>
                                {STATUS_LABEL[template.status]}
                            </span></dd>
                        </div>
                        <div className="tpl-box-r">
                            <dt>Qualidade</dt>
                            <dd>{tomQualidade(template.qualidade) === null
                                ? <span className="tpl-dash">Sem nota</span>
                                : <span className={`tpl-q ${tomQualidade(template.qualidade)}`}>
                                    <i />{rotuloQualidade(template.qualidade)}</span>}</dd>
                        </div>
                        <div className="tpl-box-r"><dt>Criado</dt><dd>{dataCurta(template.criadoEm)}</dd></div>
                        <div className="tpl-box-r">
                            <dt>ID na Meta</dt>
                            <dd className="is-mono">{template.templateMetaId || '—'}</dd>
                        </div>
                    </dl>
                </div>
            )}

            {!editando && template?.status === 'REJECTED' && !!template.motivoRejeicao && (
                <div className="tpl-box">
                    <div className="tpl-box-t">O que reprovou</div>
                    <MotivoRejeicao template={template} />
                </div>
            )}
        </aside>
    );

    /* ─── Montagem ────────────────────────────────────────────── */

    const titulo = novo ? 'Novo template' : template?.nome ?? '…';

    return (
        <WhatsAppConnectionGuard>
            <div className={`page-shell tpl-screen tpl-ed-screen ${editando ? '' : 'is-lendo'}`}>
                <PageHeader leading={voltar} title={titulo} subtitle={apoio}
                    actions={carregando ? undefined : acoes} />

                <div className="page-body tpl-ed-body">
                    <div className={`tpl-ed ${editando ? '' : 'is-lendo'}`}>
                        {/* Em edição: trilho · formulário · telefone docado. Em leitura:
                            ficha estreita · mensagem larga. São duas telas, não a
                            mesma com os campos desligados. */}
                        {editando ? trilho : !carregando && fichaLeitura}

                        <div className="tpl-col">
                            {carregando ? (
                                <p className="tpl-sec-sub">Carregando o template…</p>
                            ) : (
                                <>
                                    {novo && copiadoDe && (
                                        <p className="tpl-faixa is-wait">
                                            <LuCopy size={14} aria-hidden="true" />
                                            <span>
                                                <b>Cópia de "{copiadoDe}".</b> O nome precisa ser
                                                outro — a Meta não aceita dois templates com o
                                                mesmo nome.
                                                {ehMidia && !midiaDoOriginal && ' Reenvie também o arquivo do cabeçalho.'}
                                            </span>
                                        </p>
                                    )}

                                    {!editando && template?.status === 'REJECTED' && (
                                        <p className="tpl-faixa is-bad">
                                            <LuCircleAlert size={14} aria-hidden="true" />
                                            <span>
                                                <b>A Meta recusou este template.</b>{' '}
                                                {template.motivoRejeicao
                                                    || 'Ela não detalhou o motivo desta vez.'}
                                            </span>
                                        </p>
                                    )}

                                    {!editando && !podeEditar && template && (
                                        <p className="tpl-faixa is-wait">
                                            <LuClock size={14} aria-hidden="true" />
                                            <span>
                                                <b>{STATUS_LABEL[template.status]} na Meta.</b> Só
                                                templates aprovados ou rejeitados podem ser alterados —
                                                este está em leitura até a Meta mudar o estado dele.
                                            </span>
                                        </p>
                                    )}

                                    {editando && !novo && (
                                        <p className="tpl-faixa is-wait">
                                            <LuTriangleAlert size={14} aria-hidden="true" />
                                            <span>
                                                <b>Editar reabre a análise da Meta.</b> Até ela
                                                responder, o template continua valendo na versão
                                                aprovada — o Disparador não para.
                                            </span>
                                        </p>
                                    )}

                                    {editando ? formulario : mensagemLeitura}
                                </>
                            )}
                        </div>

                        {/* A coluna docada é do modo edição: em leitura o telefone
                            já está na coluna larga, ao lado do texto. */}
                        {editando && direita}
                    </div>
                </div>

                <Dialog
                    open={aExcluir}
                    onClose={() => setAExcluir(false)}
                    title={`Excluir o template «${template?.nome ?? ''}» na Meta?`}
                    confirmLabel="Excluir na Meta"
                    cancelLabel="Manter"
                    danger
                    busy={apagando}
                    onConfirm={excluir}
                >
                    A Meta apaga o template em <b>todos os idiomas</b> e isso <b>não tem desfazer</b> —
                    é a única exclusão do sistema que não volta.
                    {usos.length > 0
                        ? <> E <b>{usos.length} {usos.length === 1 ? 'campanha' : 'campanhas'}</b>
                            {' '}({usos.map(c => c.campanhaNome).join(', ')}){' '}
                            {usos.length === 1 ? 'aponta' : 'apontam'} para ele e{' '}
                            {usos.length === 1 ? 'para' : 'param'} de poder disparar.</>
                        : ' Nenhuma campanha aponta para ele hoje.'}
                </Dialog>
            </div>
        </WhatsAppConnectionGuard>
    );
};

export default TemplateEditorPage;
